import { useCallback, useEffect, useState } from "react";
import { api } from "services/api.service";
import { LocationInformation } from "types/locationInformation";

export function fetchLocationInformation(locationUuid?: string) {
  if (!locationUuid) return Promise.resolve(null);

  return api.get<LocationInformation>(`/location_informations/${locationUuid}`).then(
    ({ data }) => data,
    (error) => {
      if (error.response?.status === 404) return null;
      throw error;
    }
  );
}

export default function useLocationInformation(locationUuid?: string) {
  const [locationInformation, setLocationInformation] = useState<LocationInformation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const refetch = useCallback(() => {
    setIsLoading(true);

    if (!locationUuid) {
      setLocationInformation(null);
      return Promise.resolve();
    }

    setIsError(false);

    return fetchLocationInformation(locationUuid)
      .then((data) => {
        setLocationInformation(data);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [locationUuid]);

  const deleteCertificateOfInsurance = useCallback(() => {
    if (!locationUuid) return Promise.resolve();

    return api.delete(`/location_informations/${locationUuid}/certificate_of_insurance`).then(() => refetch());
  }, [locationUuid, refetch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    locationInformation,
    isLoading,
    isError,
    refetch,
    deleteCertificateOfInsurance,
  };
}
